// The map itself: the topic in the middle, the branches around it, and the
// connectors between them.
//
// HTML BOXES OVER AN SVG OF LINES, rather than one SVG of everything. The lines
// are geometry and belong in SVG; the boxes are text somebody typed, and text in
// SVG does not wrap, cannot be truncated by CSS, and is not a button. Each box
// is an ordinary `<button>` positioned at the point the layout computed, so it
// takes this design system's focus ring, its type scale and its `--c` hue
// mechanism without a single one of them being re-expressed for a canvas.
//
// IT IS A TREE, AND IT SAYS SO. `role="tree"` with one tab stop and the arrow
// keys the pattern promises - Up and Down through the whole map in reading
// order, Right into a branch, Left back out. `aria-level`, `aria-posinset` and
// `aria-setsize` carry the shape, which is the flattened form of the tree
// pattern: the boxes cannot be NESTED in the DOM, because an absolutely
// positioned element is the containing block for its own descendants and a
// nested box would be positioned against its parent's corner rather than against
// the page. So the nesting is stated rather than built, which is exactly what
// those three attributes are for.
//
// NOTHING HERE IS COLOUR-ONLY. A branch's hue says which limb it belongs to and
// nothing else; what it IS, is the words on it, and where it sits in the tree is
// announced as a level. The selected box carries `aria-selected` and a ring in
// `--ink`, not a hue - "chosen" and "which limb" are two facts and they are
// drawn in two channels.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { hueIn } from '../edit/edit-model.ts';
import type { Mode } from '../shell/theme.ts';
import {
  CENTRE,
  moveFocus,
  nudgeTo,
  positionAt,
  renderedOffsetFromCentre,
} from './realize-model.ts';
import type { Layout, PositionWrite } from './realize-model.ts';

/**
 * How far a pointer travels before it is a DRAG rather than a TAP.
 *
 * Without it every tap is a one-pixel drag: a finger never lands and lifts on
 * exactly the same point, so choosing a branch on a phone would write a position
 * each time and quietly fill the map with hand-placed boxes nobody arranged.
 * Four CSS pixels is under the threshold of a deliberate movement and over the
 * noise of a fingertip.
 */
const DRAG_THRESHOLD = 4;

interface Props {
  layout: Layout;
  /** Which box the composer is aimed at. Exactly one box is in the tab order. */
  selected: string;
  selectionNeedsConfirmation?: boolean;
  onSelect: (id: string) => void;
  /** Enter or F2 on a box: change the words on it. */
  onRename: () => void;
  /** Delete or Backspace on a branch. Never on the centre - it is not a node. */
  onDelete: () => void;
  /**
   * A branch was put somewhere, by a drag or by the arrow keys - the SAME write
   * either way, as an offset from the centre. The centre itself is never moved.
   */
  onMove: (id: string, at: PositionWrite) => void;
  movementDisabled: boolean;
  onDraggingChange: (dragging: boolean) => void;
  /**
   * A box the SCREEN wants the cursor on, which is the one focus move this
   * canvas cannot decide for itself.
   *
   * Deleting a branch unmounts the box that had focus, and the browser then puts
   * it on `<body>` - the top of the document - so a keyboard user is outside the
   * map and has to Tab back in past the topbar. Everywhere else in this tree
   * selection and focus travel together (`goTo` does both), and the delete was
   * the one selection change that moved only one of them.
   *
   * THE OBJECT'S IDENTITY IS THE ASK: a new one is a new request, the same one
   * rendered again is not. That is the consume-once rule `wanted` exists for,
   * reached from outside rather than duplicated.
   */
  focus: FocusAsk | null;
  /** Resolved once here: a map has no cap on it, so a hook per box is a hook too many. */
  mode: Mode;
}

/** One ask for the cursor, identified by the object rather than by its contents. */
export interface FocusAsk {
  id: string;
}

/** A drag in progress, which is local to this component until the pointer lifts. */
interface Dragging {
  id: string;
  pointerId: number;
  layout: Layout;
  /** Where the pointer went down, in client coordinates. */
  fromClient: { x: number; y: number };
  lastClient: { x: number; y: number };
  scroll: { element: HTMLElement; left: number; top: number };
  /** Where the box was when it went down, relative to the map's centre. */
  fromOffset: { x: number; y: number };
  /** Where it is NOW, relative to the centre, or null while it is still a tap. */
  at: { x: number; y: number } | null;
}

function pointDuringDrag(
  drag: Dragging,
  client: { x: number; y: number },
): { moved: boolean; at: { x: number; y: number } } {
  const dx = client.x - drag.fromClient.x + drag.scroll.element.scrollLeft - drag.scroll.left;
  const dy = client.y - drag.fromClient.y + drag.scroll.element.scrollTop - drag.scroll.top;
  return {
    moved: Math.hypot(dx, dy) >= DRAG_THRESHOLD,
    at: { x: drag.fromOffset.x + dx, y: drag.fromOffset.y + dy },
  };
}

export const MapCanvas = memo(function MapCanvas({
  layout,
  selected,
  selectionNeedsConfirmation = false,
  onSelect,
  onRename,
  onDelete,
  onMove,
  movementDisabled,
  onDraggingChange,
  focus,
  mode,
}: Props) {
  const boxes = useRef(new Map<string, HTMLButtonElement>());

  /**
   * The drag under the pointer, held HERE rather than sent on every move.
   *
   * A box follows the finger from local state and the server hears once, when
   * the pointer lifts. The alternative - a write per pointermove - is sixty
   * requests a second, and every one of them a row the undo of a tidy-up would
   * have to reason about.
   */
  const [drag, setDrag] = useState<Dragging | null>(null);
  const dragNow = useRef<Dragging | null>(null);
  const shown = drag?.layout ?? layout;

  const paintDrag = useCallback((held: Dragging): void => {
    if (held.at === null) return;
    const box = boxes.current.get(held.id);
    if (box === undefined) return;
    box.style.left = `${held.layout.centre.x + held.at.x}px`;
    box.style.top = `${held.layout.centre.y + held.at.y}px`;
    box.classList.add('moving');
  }, []);

  const clearPaintedDrag = useCallback((held: Dragging, restore: boolean): void => {
    const box = boxes.current.get(held.id);
    if (box === undefined) return;
    box.classList.remove('moving');
    if (!restore) return;
    const node = held.layout.nodes.find((candidate) => candidate.id === held.id);
    if (node === undefined) return;
    box.style.left = `${node.x}px`;
    box.style.top = `${node.y}px`;
  }, []);

  useEffect(() => {
    if (!movementDisabled || drag === null) return;
    clearPaintedDrag(drag, true);
    dragNow.current = null;
    setDrag(null);
    onDraggingChange(false);
  }, [clearPaintedDrag, drag, movementDisabled, onDraggingChange]);

  useEffect(() => {
    const scroller = drag?.scroll.element;
    if (scroller === undefined) return;
    const followScroll = (): void => {
      const held = dragNow.current;
      if (held === null || held.scroll.element !== scroller) return;
      const next = pointDuringDrag(held, held.lastClient);
      if (held.at === null && !next.moved) return;
      held.at = next.at;
      paintDrag(held);
    };
    scroller.addEventListener('scroll', followScroll);
    return () => scroller.removeEventListener('scroll', followScroll);
  }, [drag?.scroll.element, paintDrag]);

  /**
   * The box that was just DROPPED, so the click behind the release can be told
   * from a tap.
   *
   * A ref rather than state because it is read inside the very click handler the
   * release triggers, and a state write would not have landed by then.
   */
  const dragged = useRef<string | null>(null);

  /**
   * WHICH box has been ASKED for, once, and it is CONSUMED when it is given.
   *
   * A request rather than a state, and taken off the moment it is honoured -
   * which is the whole of it. Focus is a thing that happens, not a thing that
   * is; leaving the request standing means every later render moves focus back
   * to the box, and this screen has two places that would then fight it:
   *
   *   the map is re-read after every branch is added, and the cursor belongs in
   *   the composer where somebody is typing the next one;
   *
   *   F2 on a box hands focus to the composer to rename it - which was drawn
   *   working and then taken straight back by the render it caused.
   *
   * So `goTo` asks, and this gives it away exactly once.
   */
  const wanted = useRef<string | null>(null);

  /** The last ask HONOURED, so the same one standing is not asked again. */
  const given = useRef<FocusAsk | null>(null);

  useEffect(() => {
    // An ask from outside becomes the same one-shot request the arrow keys make,
    // so there is one path to the cursor rather than two that can disagree.
    if (focus !== null && given.current !== focus) {
      given.current = focus;
      wanted.current = focus.id;
    }
    const id = wanted.current;
    if (id === null) return;
    wanted.current = null;
    const box = boxes.current.get(id);
    if (box === undefined) return;
    box.focus();
    // The page can be bigger than the pane it scrolls in, so a box the keyboard
    // has just reached may be off screen. `nearest` scrolls the least it can,
    // which keeps the rest of the map where the eye left it.
    box.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  });

  const goTo = (id: string): void => {
    // Already there: focus now rather than asking. `onSelect` with the value it
    // already holds is a state change React is free to skip, and a request left
    // standing because the render it was waiting for never came is a request the
    // NEXT render honours - which is the composer losing the cursor.
    if (id === selected) {
      boxes.current.get(id)?.focus();
      if (selectionNeedsConfirmation) onSelect(id);
      return;
    }
    wanted.current = id;
    onSelect(id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: string): void => {
    if (
      id !== selected &&
      (event.key === 'Enter' ||
        event.key === 'F2' ||
        event.key === 'Delete' ||
        event.key === 'Backspace')
    ) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      onRename();
      return;
    }
    if ((event.key === 'Delete' || event.key === 'Backspace') && id !== CENTRE) {
      event.preventDefault();
      onDelete();
      return;
    }

    // MOVING A BOX FROM THE KEYBOARD, which is the same write a drag makes.
    //
    // Alt rather than Shift or a bare arrow: bare arrows walk the tree, which is
    // what `role="tree"` promises and may not be taken away, and Shift+Arrow is
    // spoken for by screen readers and by text selection. Alt+Arrow is free, and
    // Alt+Shift+Arrow is the same move in longer strides.
    //
    // The centre is not moved: every position on this map is an offset FROM it.
    if (event.altKey && id !== CENTRE) {
      const here = shown.nodes.find((node) => node.id === id);
      const to =
        here === undefined
          ? null
          : nudgeTo(
              here,
              shown.centre,
              event.key,
              event.shiftKey,
              shown.renderScale,
              shown.renderOriginX,
              shown.renderOriginY,
              shown.renderSpan,
            );
      if (to !== null) {
        event.preventDefault();
        if (!movementDisabled) onMove(id, to);
        return;
      }
    }

    const next = moveFocus(shown, id, event.key);
    // Null means the key means nothing here, and the browser keeps it: Left on
    // the centre is still whatever Left does, rather than being swallowed.
    if (next === null) return;
    event.preventDefault();
    goTo(next);
  };

  /**
   * DRAGGING, ON POINTER EVENTS, WHICH IS THE WHOLE REASON IT WORKS BY TOUCH.
   *
   * One code path for mouse, finger and pen: `pointerdown` / `pointermove` /
   * `pointerup` with `setPointerCapture`, rather than mouse handlers with touch
   * handlers bolted beside them. A drag implementation that only listens for
   * mouse events silently excludes the primary device this product is used on,
   * and it is the kind of gap that is invisible on the machine it was written on.
   *
   * `setPointerCapture` is what makes a fast drag survive the pointer leaving
   * the box: without it the events go to whatever is under the cursor and the
   * box is dropped wherever the finger outran it.
   *
   * The centre is not draggable - every position is an offset from it - and a
   * secondary mouse button is left alone so a right-click is still a right-click.
   */
  const onPointerDown = (event: PointerEvent<HTMLButtonElement>, id: string): void => {
    if (movementDisabled || id === CENTRE || event.button !== 0) return;
    const here = shown.nodes.find((node) => node.id === id);
    const scroller = event.currentTarget.closest<HTMLElement>('.rzcanvas');
    if (here === undefined || scroller === null) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    onDraggingChange(true);
    const held: Dragging = {
      id,
      pointerId: event.pointerId,
      layout: shown,
      fromClient: { x: event.clientX, y: event.clientY },
      lastClient: { x: event.clientX, y: event.clientY },
      scroll: { element: scroller, left: scroller.scrollLeft, top: scroller.scrollTop },
      fromOffset: renderedOffsetFromCentre(here, shown.centre),
      // Null until the pointer has actually travelled: until then this is a tap
      // that happens to be holding still, and the click that follows selects.
      at: null,
    };
    dragNow.current = held;
    setDrag({ ...held });
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
    const held = dragNow.current;
    if (held === null || event.pointerId !== held.pointerId) return;
    held.lastClient = { x: event.clientX, y: event.clientY };
    const next = pointDuringDrag(held, held.lastClient);
    if (held.at === null && !next.moved) return;
    // Past the threshold: from here the box follows the pointer, and the click
    // that would otherwise fire on release is suppressed below.
    held.at = next.at;
    paintDrag(held);
  };

  const onPointerUp = (event: PointerEvent<HTMLButtonElement>): void => {
    const held = dragNow.current;
    if (held === null || event.pointerId !== held.pointerId) return;
    const next = pointDuringDrag(held, { x: event.clientX, y: event.clientY });
    const landed = held.at === null && !next.moved ? null : next.at;
    clearPaintedDrag(held, movementDisabled);
    dragNow.current = null;
    setDrag(null);
    onDraggingChange(false);
    // A tap, not a drag: nothing is written, and the click event that follows
    // this one chooses the branch exactly as it always did.
    if (landed === null || movementDisabled) return;
    dragged.current = held.id;
    boxes.current.get(selected)?.focus();
    const source = held.layout.nodes.find((node) => node.id === held.id);
    if (source !== undefined) {
      onMove(
        held.id,
        positionAt(
          source,
          held.layout.centre,
          landed,
          held.layout.renderScale,
          held.layout.renderOriginX,
          held.layout.renderOriginY,
          held.layout.renderSpan,
        ),
      );
    }
  };

  const onPointerCancel = (event: PointerEvent<HTMLButtonElement>): void => {
    const held = dragNow.current;
    if (held === null || event.pointerId !== held.pointerId) return;
    clearPaintedDrag(held, true);
    dragNow.current = null;
    dragged.current = null;
    setDrag(null);
    onDraggingChange(false);
    boxes.current.get(selected)?.focus();
  };

  /**
   * Where a box is drawn RIGHT NOW, which is its dragged position while a finger
   * is on it and its placed one otherwise.
   *
   * Painted directly while held rather than re-rendering the uncapped tree on
   * every pointer move; the server still hears only the final position.
   */
  const pointOf = (id: string, placed: { x: number; y: number }): { x: number; y: number } => {
    const held = dragNow.current;
    return held !== null && held.id === id && held.at !== null
      ? { x: shown.centre.x + held.at.x, y: shown.centre.y + held.at.y }
      : placed;
  };

  return (
    <div
      className="rzmap"
      role="tree"
      aria-label="Mind map"
      style={{ width: `${shown.width}px`, height: `${shown.height}px` }}
    >
      {/* Decoration: every connector is a line between two boxes that are both
          on this page and both announced, so a screen reader reading the lines
          as well would read the map twice. */}
      <svg
        className="rzlinks"
        viewBox={`0 0 ${shown.width} ${shown.height}`}
        width={shown.width}
        height={shown.height}
        aria-hidden="true"
        fill="none"
      >
        {shown.links.map((link) => (
          <path
            key={link.id}
            d={link.d}
            className={`rzlink d${Math.min(link.depth, 3)}`}
            style={
              link.colorHex === null
                ? undefined
                : ({ '--c': hueIn(link.colorHex, mode) } as CSSProperties)
            }
          />
        ))}
      </svg>

      {shown.nodes.map((node) => (
        <button
          key={node.id}
          ref={(element) => {
            if (element === null) boxes.current.delete(node.id);
            else boxes.current.set(node.id, element);
          }}
          className={[
            'rznode',
            `d${Math.min(node.depth, 2)}`,
            node.id === selected ? 'on' : '',
            dragNow.current?.id === node.id && dragNow.current.at !== null ? 'moving' : '',
            node.placedByHand ? 'byhand' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          role="treeitem"
          // The centre is level 1 and a branch off it is level 2: the tree's own
          // depth, one-based, which is what the pattern counts.
          aria-level={node.depth + 1}
          aria-posinset={node.position}
          aria-setsize={node.siblings}
          aria-selected={node.id === selected}
          // ONE TAB STOP for the whole map, which is what `role="tree"` means:
          // Tab reaches the map and then leaves it for the composer, and the
          // arrows move inside it. Fifty branches must not be fifty tab presses
          // between the map and the box you type into.
          tabIndex={node.id === selected ? 0 : -1}
          style={
            {
              left: `${pointOf(node.id, node).x}px`,
              top: `${pointOf(node.id, node).y}px`,
              ...(node.colorHex === null ? {} : { '--c': hueIn(node.colorHex, mode) }),
            } as CSSProperties
          }
          onPointerDown={(event) => onPointerDown(event, node.id)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onClick={(event) => {
            // The click that follows a real drag is not a choice - it is the
            // release. Without this, dropping a box also re-aims the composer,
            // which is a second thing happening for one gesture.
            if (dragged.current === node.id) {
              dragged.current = null;
              event.preventDefault();
              return;
            }
            goTo(node.id);
          }}
          onKeyDown={(event) => onKeyDown(event, node.id)}
        >
          <span className="rzlabel" aria-hidden="true">
            {node.heading}
          </span>
          <span className="sr">
            {node.label.trim() === '' ? node.heading : node.label}
            {node.childCount > 0 &&
              `, ${node.childCount} ${node.childCount === 1 ? 'branch' : 'branches'} under it`}
          </span>
          {node.childCount > 0 && (
            <span className="rzn tnum" aria-hidden="true">
              {node.childCount}
            </span>
          )}
        </button>
      ))}
    </div>
  );
});
